import { isString } from 'lodash';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { useDropzone } from 'react-dropzone';
import fileFill from '@iconify/icons-eva/file-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import { motion, AnimatePresence } from 'framer-motion';
// material
import { alpha, experimentalStyled as styled } from '@material-ui/core/styles';
import {
  Box,
  List,
  Link,
  Stack,
  Paper,
  Button,
  ListItem,
  Typography,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Card,
  Skeleton,
  makeStyles,
  CircularProgress,
  LinearProgress,
  Tooltip,
  Checkbox,
} from '@material-ui/core';
import Label from '../Label';
// utils
import { fData } from '../../utils/formatNumber';
//
import { MIconButton } from '../@material-extend';
import { DialogAnimate, varFadeInRight } from '../animate';
import { UploadIllustration } from '../../assets';
import { PictureAsPdf, PictureAsPdfOutlined, Description, GetAppOutlined } from '@material-ui/icons';
import { LoadingButton } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import filePdfIcon from '@iconify/icons-mdi/file-pdf';
import { useSnackbar } from 'notistack';
import {
  downloadFileFromUrl,
  downloadImage,
  downloadAllAttachments,
  openFileInViewer,
  isOfficeFile,
  isPdfFile,
  isImageFile,
  getFileExtension,
  getAttachmentDownloadUrl,
  getAttachmentDisplayName,
  resolveDownloadFileName,
  getEmbeddedFileSource,
} from 'src/Helpers/FileDownloadUtils';

// ----------------------------------------------------------------------
const useStyles = makeStyles((theme) => ({
  attachmentBox: {
      cursor: 'pointer',
      display: 'flex',
      borderRadius: '6px',
      backgroundColor: '#172e34',
      padding: theme.spacing(1),
      margin: theme.spacing(1, 0),
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative', 
      height: '100px', 
      overflow: 'hidden'
  },
  attachmentIcon: {
      marginRight: theme.spacing(1),
  },
  delete:{
      m:2,
      display:'flex',
      justifyContent:'flex-end' 
    },
  checkbox: {
      position: 'absolute',
      top: theme.spacing(1), 
      right: theme.spacing(1), 
  },
  checkboxContainer: {
      position: 'absolute',
      top: theme.spacing(1),
      right: theme.spacing(1),
  },
  dialogContent: {
      padding: theme.spacing(3),
      maxWidth: 768,
      margin: 'auto',
      display: 'flex',
      flexDirection:'column',
      justifyContent: 'center',
      alignItems: 'center',
  },
  gridContainer: {
      height: 'auto',
  },
}));

const DropZoneStyle = styled('div')(({ theme }) => ({
  outline: 'none',
  display: 'flex',
  textAlign: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(5, 1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.neutral,
  border: `1px dashed ${theme.palette.grey[500_32]}`,
  '&:hover': { opacity: 0.72, cursor: 'pointer' },
  [theme.breakpoints.up('md')]: { textAlign: 'left', flexDirection: 'row' }
}));

// ----------------------------------------------------------------------

UploadMultiFile.propTypes = {
  error: PropTypes.bool,
  showPreview: PropTypes.bool,
  files: PropTypes.array,
  onRemove: PropTypes.func,
  onRemoveAll: PropTypes.func,
  sx: PropTypes.object,
  showProductLabels: PropTypes.bool,
  allowDownload: PropTypes.bool,
};

export default function UploadMultiFile({ error, showPreview = false, files,singleFile, onRemove, onRemoveAll,isUploading = false, showProductLabels = false, allowDownload = true, sx, ...other }) {
  const hasFile = files?.length > 0;
  const uploadActionsDisabled = other.disabled === true;
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedPDF, setSelectedPDF] = useState(null);
  const [selectedFileMeta, setSelectedFileMeta] = useState(null);
  const [openPDFModal, setOpenPDFModal] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [useOfficeViewer, setUseOfficeViewer] = useState(true);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [selectedDownloadKeys, setSelectedDownloadKeys] = useState([]);

    // Get user data from localStorage
    const user = JSON.parse(localStorage.getItem('user'));
    const isClient = user?.isClient;
    const userType = isClient ? 3 : user?.user_type;
    const userId = user?.user_id;
    const canDownload = allowDownload && !isClient && userType !== 3;

  const getFileSelectKey = (file, index = 0) => {
    if (isString(file)) return file;
    const url =
      getAttachmentDownloadUrl(file) ||
      file?.url ||
      file?.name ||
      file?.preview ||
      '';
    if (url) return String(url);
    if (file instanceof File) {
      return `local:${file.name}:${file.size}:${file.lastModified}`;
    }
    return `idx:${index}`;
  };

  const resolveFileUrl = (file) => {
    if (!file) return '';
    if (isString(file)) return file;
    return getAttachmentDownloadUrl(file) || '';
  };

  const handleToggleDownloadSelect = (file, index, checked) => {
    const key = getFileSelectKey(file, index);
    setSelectedDownloadKeys((prev) =>
      checked ? [...new Set([...prev, key])] : prev.filter((k) => k !== key),
    );
  };

  const selectableFileKeys = (files || []).map((file, index) =>
    getFileSelectKey(file, index),
  );
  const allFilesSelected =
    selectableFileKeys.length > 0 &&
    selectableFileKeys.every((key) => selectedDownloadKeys.includes(key));
  const someFilesSelected =
    selectedDownloadKeys.length > 0 && !allFilesSelected;

  const handleSelectAllDownloads = (checked) => {
    if (checked) {
      setSelectedDownloadKeys(selectableFileKeys);
      return;
    }
    setSelectedDownloadKeys([]);
  };

  useEffect(() => {
    const validKeys = new Set(
      (files || []).map((file, index) => getFileSelectKey(file, index)),
    );
    setSelectedDownloadKeys((prev) => prev.filter((key) => validKeys.has(key)));
  }, [files]);

  const handleDownloadFile = (fileUrl, file = null, fileIndex = 0) => {
    if (!fileUrl) {
      enqueueSnackbar('No file to download', { variant: 'warning' });
      return;
    }
    const fileType = file?.file_type || file?.type || '';
    const ext = getFileExtension(fileUrl);
    const onSuccess = () => enqueueSnackbar('File downloaded successfully', { variant: 'success' });
    const onError = () => enqueueSnackbar('Failed to download file', { variant: 'error' });

    const displayName = isString(file) ? null : getAttachmentDisplayName(file);

    if (
      isImageFile(fileType) ||
      ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)
    ) {
      downloadImage(fileUrl, displayName, fileIndex, onSuccess, onError);
      return;
    }
    downloadFileFromUrl(fileUrl, displayName, onSuccess, onError);
  };

  const handleDownloadSelected = async () => {
    const selectedFiles = (files || []).filter((file, index) =>
      selectedDownloadKeys.includes(getFileSelectKey(file, index)),
    );

    if (!selectedFiles.length) {
      enqueueSnackbar('Please select files to download', { variant: 'warning' });
      return;
    }

    setIsDownloadingAll(true);
    enqueueSnackbar(
      `Downloading ${selectedFiles.length} file${selectedFiles.length === 1 ? '' : 's'}...`,
      { variant: 'info' },
    );

    try {
      // Local File objects (not yet uploaded) — download via blob URL
      const localFiles = selectedFiles.filter(
        (file) => file instanceof File || file instanceof Blob,
      );
      const remoteFiles = selectedFiles.filter(
        (file) => !(file instanceof File || file instanceof Blob),
      );

      let successCount = 0;
      let failCount = 0;

      for (let i = 0; i < localFiles.length; i += 1) {
        const file = localFiles[i];
        try {
          const blobUrl = URL.createObjectURL(file);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = file.name || `file-${i + 1}`;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
          successCount += 1;
        } catch (error) {
          console.error('Local file download failed:', error);
          failCount += 1;
        }
        if (i < localFiles.length - 1 || remoteFiles.length > 0) {
          await new Promise((resolve) => setTimeout(resolve, 400));
        }
      }

      if (remoteFiles.length > 0) {
        const remoteResult = await downloadAllAttachments(remoteFiles);
        successCount += remoteResult.successCount;
        failCount += remoteResult.failCount;
      }

      const total = selectedFiles.length;
      if (successCount === total) {
        enqueueSnackbar(
          `Downloaded ${successCount} file${successCount === 1 ? '' : 's'} successfully`,
          { variant: 'success' },
        );
        setSelectedDownloadKeys([]);
      } else if (successCount > 0) {
        enqueueSnackbar(
          `Downloaded ${successCount} of ${total} files. ${failCount} failed.`,
          { variant: 'warning' },
        );
        setSelectedDownloadKeys([]);
      } else {
        enqueueSnackbar('Failed to download files', { variant: 'error' });
      }
    } catch (error) {
      console.error('Download selected failed:', error);
      enqueueSnackbar('Failed to download files', { variant: 'error' });
    } finally {
      setIsDownloadingAll(false);
    }
  };

  const handleOpenFileViewer = (fileUrl, file = null) => {
    if (!fileUrl) return;
    const fileName =
      getAttachmentDisplayName(file) ||
      resolveDownloadFileName(fileUrl) ||
      'File';
    const fileType = file?.file_type || file?.type || '';
    openFileInViewer(fileUrl, {
      isActualPdf: isPdfFile(fileUrl, fileType),
      isOfficeFile: isOfficeFile(fileUrl, fileType),
      isClient: isClient || userType === 3,
      hideToolbar: true,
      fileName,
      fileType,
    });
  };
    
  const blockedExtensions = [
    'js', 'jsx', 'ts', 'tsx',
    'html', 'css', 'php', 'py', 'java',
    'c', 'cpp', 'cs', 'sh', 'bat',
    'json', 'xml', 'sql', 'db', 'sqlite',
    'jmx', 'env', 'ini', 'config', 'yml', 'yaml'
  ];

  const validateFile = (file) => {
    const ext = file.name.split('.').pop()?.toLowerCase();

    if (blockedExtensions.includes(ext)) {
      return {
        code: 'file-type-not-allowed',
        message: `.${ext} files are not allowed`
      };
    }

    return null;
  };
    
  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    multiple: other.multiple ?? true,   // default: true
    validator: validateFile,
    ...other
  });

  const isSingle = other.multiple === false;
  const isSingleFile = singleFile === true;

  const ShowRejectionItems = () => (
    <Paper
      variant="outlined"
      sx={{
        py: 1,
        px: 2,
        mt: 3,
        borderColor: 'error.light',
        bgcolor: (theme) => alpha(theme.palette.error.main, 0.08)
      }}
    >
      {fileRejections?.map(({ file, errors }) => {
        const { path, size } = file;
        return (
          <Box key={path} sx={{ my: 1 }}>
            <Typography variant="subtitle2" noWrap>
              {path} - {fData(size)}
            </Typography>
            {errors.map((e) => (
              <Typography key={e.code} variant="caption" component="p">
                - {e.message}
              </Typography>
            ))}
          </Box>
        );
      })}
    </Paper>
  );

  return (
    <Box sx={{ width: '100%', ...sx }}>
      {/* {isUploading ?
        <LinearProgress variant='indeterminate' color='primary' />
      : */}
        <DropZoneStyle
          {...getRootProps()}
          sx={{
            ...(isDragActive && { opacity: 0.72 }),
            ...((isDragReject || error) && {
              color: 'error.main',
              borderColor: 'error.light',
              bgcolor: 'error.lighter'
            }),
            ...(isSingle && !singleFile && {
              padding: '24px 8px',          // ↓ Reduced padding
              minHeight: '140px'            // ↓ Shorter height
            }),
            ...(uploadActionsDisabled && {
              opacity: 0.5,
              pointerEvents: 'none',
              cursor: 'not-allowed',
            }),
          }}
        >
          <input {...getInputProps()} />

          <UploadIllustration sx={{ width: isSingle ? 140 : singleFile ? 140 : 220 }} />

          <Box sx={{ p: isSingle ? 1 : singleFile ? 1 : 3, ml: { md: isSingle ? 1 : singleFile ? 1 : 2 } }}>
            <Typography gutterBottom variant="h5">
              {isSingle ? "Drop or Select a file" : singleFile ? "Drop or Select a file" : "Drop or Select files"}
            </Typography>

            <Typography variant="body2" >
            {/* <Typography variant="body2" sx={{ color: 'text.secondary' }}> */}
              Drop {isSingle ? 'file' : singleFile ? 'file' : 'files'} here or click&nbsp;{singleFile ? '' : ''}
              <Link underline="always">browse</Link>&nbsp;through your machine
            </Typography>
            {error && (
              <Typography variant="caption" sx={{ color: 'error.main' }}>
                {isString(error) ? error : error.message}
              </Typography>
            )}
            {/* <Typography variant="caption" sx={{ color: 'error.main' }}>
            Maximum file size: 15MB
            </Typography> */}
          </Box>
        </DropZoneStyle>
      {/* } */}
      {fileRejections?.length > 0 && <ShowRejectionItems />}

      <List
        disablePadding
        sx={{
          ...(hasFile && { my: 3 }),
          bgcolor: 'transparent !important',
          backgroundColor: 'transparent !important',
        }}
      >
        <AnimatePresence>
          {(isSingle ? files.slice(0, 1) : singleFile ? files.slice(0, 1) : files)?.map((file, fileIndex) => {
            const { size, preview, file_type, extension } = file;
            const url = file.url || file.name;
            const key = isString(file) ? file : url;
            const openPreviewModal = (previewUrl) => {
              setPdfLoading(true);
              setSelectedPDF(previewUrl);
              setSelectedFileMeta(isString(file) ? null : file);
              setOpenPDFModal(true);
              setUseOfficeViewer(true);
            };
            if (showPreview) {
              const productNames = file?.productNames || [];
              const serviceNames = file?.serviceNames || [];
              const isGroupAttachment = !!file?._groupAttachment;
              const showProductLabel =
                showProductLabels &&
                file?._productAttachment &&
                productNames.length > 0;
              const showServiceLabel =
                showProductLabels && file?._serviceAttachment && serviceNames.length > 0;
              const showLabel = showProductLabel || showServiceLabel;
              
              return (
                <ListItem
                  key={key}
                  component={motion.div}
                  {...varFadeInRight}
                  sx={{
                    p: 0,
                    m: 0.5,
                    width: 80,
                    height: 80,
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    position: 'relative',
                    display: 'inline-flex',
                    flexDirection: 'column'
                  }}
                >
                  {showLabel && (
                    <Box sx={{ 
                      position: 'absolute', 
                      top: 0, 
                      left: 0, 
                      right: 0, 
                      zIndex: 10,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'flex-start'
                    }}>
                      <Tooltip
                        title={
                          showServiceLabel
                            ? `Linked with service: ${serviceNames
                                .map((s, index) => `(${index + 1}). ${s?.service_name || ''}`)
                                .join(', ')}`
                            : isGroupAttachment
                              ? `Group: ${productNames[0]?.product_name || 'Group'}`
                              : `Linked with: ${[...new Map(productNames.map((p) => [p.product_id, p])).values()]
                                  .map((p, index) => `(${index + 1}). ${p?.product_name}`)
                                  .join(', ')}`
                        }
                        arrow
                      >
                        <span>
                          <Label
                            variant={'filled'}
                            color={showServiceLabel ? 'success' : 'info'}
                            style={{ 
                              color: 'white', 
                              height: 18, 
                              fontSize: '9px',
                              padding: '2px 6px',
                              maxWidth: '76px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {(() => {
                              if (showServiceLabel) {
                                const fullString = serviceNames.map((s) => s?.service_name || '').filter(Boolean).join(', ');
                                return fullString.length > 12
                                  ? fullString.substring(0, 10) + '...'
                                  : fullString;
                              }
                              if (isGroupAttachment) {
                                const groupTitle = productNames[0]?.product_name || 'Group';
                                return groupTitle.length > 12
                                  ? groupTitle.substring(0, 10) + '...'
                                  : groupTitle;
                              }
                              const uniqueNames = [...new Map(productNames.map((p) => [p.product_id, p.product_name])).values()];
                              const fullString = uniqueNames.join(', ');
                              return fullString.length > 12 
                                ? fullString.substring(0, 10) + '...' 
                                : fullString;
                            })()}
                          </Label>
                        </span>
                      </Tooltip>
                    </Box>
                  )}
                  {(() => {
                    const computedUrl = url || file.name || '';
                    // Proxy URLs end with `/file?path=...xlsx` — take ext from path=, not last URL segment.
                    const fileExt =
                      getFileExtension(computedUrl) ||
                      (extension || '').toLowerCase() ||
                      '';
                    const displayLabel =
                      getAttachmentDisplayName(file) ||
                      resolveDownloadFileName(computedUrl) ||
                      'Doc';

                    const isActualPdf = file_type === 'pdf' || file_type === 'application/pdf' || extension === 'pdf' || file?.type === 'application/pdf' || fileExt === 'pdf';
                    const isImage = file?.type?.startsWith('image/') || file_type?.startsWith('image/') || file_type === 'img' || file_type === 'image' || ['jpg','jpeg','png','gif','svg','webp'].includes(extension?.toLowerCase()) || ['jpg','jpeg','png','gif','svg','webp'].includes(fileExt);

                    const googleViewerFormats = ['eps','ai','psd','tif','tiff','bmp','ps','indd','indt'];
                    const isGoogleViewer = googleViewerFormats.includes(fileExt);
                    const officeViewerFormats = ['doc','docx','xls','xlsx','ppt','pptx','csv'];
                    const isOfficeFileType =
                      isOfficeFile(computedUrl, file_type || file?.type) ||
                      officeViewerFormats.includes(fileExt);
                    const isPublicUrl = typeof computedUrl === 'string' && computedUrl.startsWith('http');

                    if (isActualPdf) {
                      return (
                        <IconButton onClick={() => openPreviewModal(isString(url) ? url : preview)}
                          aria-label="open pdf"
                          sx={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute' }} >
                          <iframe
                            src={isString(url) ? url : preview}
                            title="PDF Viewer"
                            frameBorder="0"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', pointerEvents: 'none' }}
                          />
                        </IconButton>
                        // <DialogAnimate open={openPDFModal} onClose={() => setOpenPDFModal(false)}>
                        //   <Card className={classes.dialogContent}>
                        //     <div style={{width:'90%', display:'flex',justifyContent:'flex-end', marginBottom: '15px',marginLeft:'25px' }}>
                        //         <Button
                        //           variant="outlined"
                        //           color="primary"
                        //           onClick={() => {
                        //             if (isClient || userType === 3) {
                        //           // For clients/userType 3: Open PDF in new window without toolbar
                        //               const newWindow = window.open('', '_blank');
                        //               newWindow.document.write(`
                        //                 <html>
                        //                   <head>
                        //                     <title>PDF Viewer</title>
                        //                     <style>
                        //                       body { margin: 0; padding: 0; overflow: hidden; }
                        //                       iframe { width: 100vw; height: 100vh; border: none; }
                        //                     </style>
                        //                   </head>
                        //                   <body>
                        //                     <iframe src="${selectedPDF}#toolbar=0&navpanes=0&scrollbar=0" title="PDF Viewer"></iframe>
                        //                   </body>
                        //                 </html>
                        //               `);
                        //               newWindow.document.close();
                        //             } else {
                        //           // For other users: Open PDF normally
                        //               window.open(selectedPDF, '_blank');
                        //             }
                        //           }}
                        //           disabled={!selectedPDF}
                        //           startIcon={<Icon icon={filePdfIcon} width={20} height={20} />}
                        //           sx={{
                        //             fontSize:'14px'
                        //           }}
                        //         >
                        //           View PDF
                        //         </Button>
                        //     </div>
                        //     {pdfLoading && (
                        //       <Skeleton variant="rectangular" width="100%" height={500} />
                        //     )}
                        //     {(isClient || userType === 3) && useOfficeViewer ? (
                        //       <iframe
                        //         src={`https://docs.google.com/gview?url=${encodeURIComponent(selectedPDF)}&embedded=true&toolbar=0&navpanes=0&scrollbar=0`}
                        //         title="Selected PDF"
                        //         width="100%"
                        //         height="500px"
                        //         frameBorder="0"
                        //         style={{ display: pdfLoading ? 'none' : 'block' }}
                        //         onLoad={() => setPdfLoading(false)}
                        //         onError={() => {
                        //       // If Google Docs viewer fails, fallback to direct iframe
                        //           setUseOfficeViewer(false);
                        //           setPdfLoading(false);
                        //         }}
                        //       />
                        //     ) : (
                        //       <iframe
                        //         src={selectedPDF}
                        //         title="Selected PDF"
                        //         width="100%"
                        //         height="500px"
                        //         frameBorder="0"
                        //         style={{ display: pdfLoading ? 'none' : 'block' }}
                        //         onLoad={() => setPdfLoading(false)}
                        //       />
                        //     )}
                        //   </Card>
                        // </DialogAnimate>
                        // </>
                      );
                    } else if (isPublicUrl && (isGoogleViewer || isOfficeFileType)) {
                      const viewerSrc = isOfficeFileType
                        ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(computedUrl)}`
                        : `https://docs.google.com/gview?url=${encodeURIComponent(computedUrl)}&embedded=true`;
                      return (
                        <Box
                          onClick={() => openPreviewModal(computedUrl)}
                          sx={{ width: '100%', height: '100%', position: 'absolute', cursor: 'pointer', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <iframe
                            src={viewerSrc}
                            title="thumbnail preview"
                            width="300%"
                            height="300%"
                            frameBorder="0"
                            style={{ border: 'none', pointerEvents: 'none', transform: 'scale(0.33)' }}
                           sandbox="allow-scripts allow-same-origin" />
                        </Box>
                      );
                    } else if (!isImage && !isGoogleViewer) {
                      return (
                        <Box
                          onClick={() => openPreviewModal(isString(url) ? url : preview)}
                          sx={{ width: '100%', height: '100%', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.neutral', position: 'absolute' }}>
                          <Description style={{ fontSize: 30, color: 'gray' }} />
                          <Typography variant="caption" noWrap sx={{ px: 1, mt: 0.5, textAlign: 'center', width: '100%' }} title={displayLabel}>
                            {displayLabel}
                          </Typography>
                        </Box>
                      );
                    } else {
                      return (
                        <Paper
                          variant="outlined"
                          component="img"
                          src={isString(url) ? url : preview}
                          onClick={() => openPreviewModal(isString(url) ? url : preview)}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', cursor: 'pointer' }}
                          onError={(e) => {
                            if (preview !== url && typeof url === 'string') {
                              e.target.src = url;
                            }
                          }}
                        />
                      );
                    }
                  })()}
                  {canDownload && !isSingle && !singleFile && (
                    <Checkbox
                      size="small"
                      checked={selectedDownloadKeys.includes(getFileSelectKey(file, fileIndex))}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleToggleDownloadSelect(file, fileIndex, e.target.checked);
                      }}
                      sx={{
                        position: 'absolute',
                        top: 2,
                        left: 2,
                        zIndex: 12,
                        p: 0.25,
                        color: 'common.white',
                        bgcolor: 'rgba(255, 255, 255, 0.85)',
                        borderRadius: '4px',
                        '&.Mui-checked': {
                          color: 'primary.main',
                        },
                      }}
                    />
                  )}
                  {canDownload && (
                    <Box sx={{ bottom: 6, left: 6, position: 'absolute', zIndex: 11 }}>
                      <Tooltip title="Download">
                        <MIconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadFile(resolveFileUrl(file), isString(file) ? null : file, fileIndex);
                          }}
                          sx={{
                            p: '2px',
                            color: 'common.white',
                            bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
                            '&:hover': {
                              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48)
                            }
                          }}
                        >
                          <GetAppOutlined fontSize="small" />
                        </MIconButton>
                      </Tooltip>
                    </Box>
                  )}
                  {onRemove && !uploadActionsDisabled && (
                    <Box sx={{ top: 6, right: 6, position: 'absolute', zIndex: 11 }}>
                      <MIconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemove(file);
                        }}
                        sx={{
                          p: '2px',
                          color: 'common.white',
                          bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
                          '&:hover': {
                            bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48)
                          }
                        }}
                      >
                        <Icon icon={closeFill} />
                      </MIconButton>
                    </Box>
                  )}
                </ListItem>
              );
            }

            return (
              <ListItem
                key={key}
                component={motion.div}
                {...varFadeInRight}
                sx={{
                  my: 1,
                  py: 0.75,
                  px: 2,
                  borderRadius: 1,
                  border: (theme) => `solid 1px ${theme.palette.divider}`,
                  bgcolor: 'transparent'
                }}
              >
                {canDownload && !isSingle && !singleFile && (
                  <Checkbox
                    size="small"
                    checked={selectedDownloadKeys.includes(getFileSelectKey(file, fileIndex))}
                    onChange={(e) =>
                      handleToggleDownloadSelect(file, fileIndex, e.target.checked)
                    }
                    sx={{ mr: 1 }}
                  />
                )}
                <ListItemIcon>
                  <Icon icon={fileFill} width={28} height={28} />
                </ListItemIcon>
                <ListItemText
                  primary={isString(file) ? file : url}
                  secondary={isString(file) ? '' : fData(size)}
                  primaryTypographyProps={{ variant: 'subtitle2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
                <ListItemSecondaryAction>
                  {!uploadActionsDisabled && (
                  <MIconButton edge="end" size="small" onClick={() => onRemove(file)}>
                    <Icon icon={closeFill} />
                  </MIconButton>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </AnimatePresence>
      </List>

      {(hasFile && !isSingle && !singleFile) && (
        <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={uploadActionsDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}>
          {canDownload && selectedDownloadKeys.length > 0 && (
            <Tooltip title={allFilesSelected ? 'Deselect All' : 'Select All'}>
              <Checkbox
                size="small"
                checked={allFilesSelected}
                indeterminate={someFilesSelected}
                onChange={(e) => handleSelectAllDownloads(e.target.checked)}
                disabled={uploadActionsDisabled}
                sx={{ mr: 0.5 }}
              />
            </Tooltip>
          )}
          {canDownload && (
            <Tooltip title="Download Selected">
              <span>
                <Button
                  onClick={handleDownloadSelected}
                  disabled={
                    uploadActionsDisabled ||
                    isDownloadingAll ||
                    selectedDownloadKeys.length === 0
                  }
                  sx={{
                    mr: 1.5,
                    minWidth: 'auto',
                    ...(uploadActionsDisabled && { cursor: 'not-allowed !important' }),
                  }}
                >
                  <GetAppOutlined />
                </Button>
              </span>
            </Tooltip>
          )}
          <Button onClick={onRemoveAll} disabled={uploadActionsDisabled} sx={{ mr: 1.5, ...(uploadActionsDisabled && { cursor: 'not-allowed !important' }) }}>
            Remove all
          </Button>
          <LoadingButton
            {...(uploadActionsDisabled ? {} : getRootProps())}
            variant="contained"
            loading={isUploading}
            disabled={uploadActionsDisabled}
            sx={uploadActionsDisabled ? { cursor: 'not-allowed !important' } : {}}
            >
            Upload files
          </LoadingButton>
          {/* <Button {...getRootProps()} variant="contained">Upload files</Button> */}
        </Stack>
      )}
      <DialogAnimate open={openPDFModal} onClose={() => { setOpenPDFModal(false); setSelectedFileMeta(null); }}>
        <Card className={classes.dialogContent}>
          <div style={{ width: '90%', display: 'flex', justifyContent: 'flex-end', marginBottom: '15px', marginLeft: '25px', gap: '10px' }}>
            {canDownload && (
              <Tooltip title="Download File">
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => handleDownloadFile(selectedPDF, selectedFileMeta)}
                  disabled={!selectedPDF}
                  startIcon={<GetAppOutlined />}
                  sx={{ fontSize: '14px' }}
                >
                  Download
                </Button>
              </Tooltip>
            )}
            <Button
              variant="outlined"
              color="primary"
              onClick={() => handleOpenFileViewer(selectedPDF, selectedFileMeta)}
              disabled={!selectedPDF}
              startIcon={<Icon icon={filePdfIcon} width={20} height={20} />}
              sx={{ fontSize: '14px' }}
            >
              {(() => {
                const fileType = selectedFileMeta?.file_type || selectedFileMeta?.type || '';
                if (isPdfFile(selectedPDF, fileType)) return 'View PDF';
                if (isImageFile(fileType)) return 'View Image';
                return 'View File';
              })()}
            </Button>
          </div>
          {pdfLoading && (
            <Skeleton variant="rectangular" width="100%" height={500} />
          )}
          {(() => {
            if (!selectedPDF) return null;
            const metaExt = (selectedFileMeta?.extension || '').toLowerCase();
            const fileType = selectedFileMeta?.file_type || selectedFileMeta?.type || '';
            const fileExt = getFileExtension(selectedPDF) || metaExt;
            const isOffice =
              isOfficeFile(selectedPDF, fileType) ||
              ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'csv'].includes(metaExt);
            const isDesign = ['ai', 'psd', 'eps', 'dwg', 'dxf', 'tif', 'tiff', 'bmp'].includes(fileExt);
            const isImage = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(fileExt);

            if (isImage) {
              return (
                <img
                  src={selectedPDF}
                  alt="Preview"
                  style={{ maxWidth: '100%', maxHeight: '500px', objectFit: 'contain', display: pdfLoading ? 'none' : 'block' }}
                  onLoad={() => setPdfLoading(false)}
                />
              );
            }

            if (isOffice) {
              return (
                <iframe
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(selectedPDF)}`}
                  title="Office Viewer"
                  width="100%"
                  height="500px"
                  frameBorder="0"
                  style={{ display: pdfLoading ? 'none' : 'block' }}
                  onLoad={() => setPdfLoading(false)}
                />
              );
            }

            if (isDesign || (isClient || userType === 3)) {
              const embeddedSrc = getEmbeddedFileSource(selectedPDF, {
                isActualPdf: fileExt === 'pdf',
                hideToolbar: true,
              });
              return (
                <iframe
                  src={embeddedSrc}
                  title="Google Viewer"
                  width="100%"
                  height="500px"
                  frameBorder="0"
                  style={{ display: pdfLoading ? 'none' : 'block' }}
                  onLoad={() => setPdfLoading(false)}
                  onError={() => {
                    setPdfLoading(false);
                  }}
                />
              );
            }

            // Never iframe raw xlsx/doc/etc — browser downloads instead of previewing.
            if (fileExt && fileExt !== 'pdf') {
              Promise.resolve().then(() => setPdfLoading(false));
              return (
                <Box
                  sx={{
                    width: '100%',
                    height: 500,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <Description style={{ fontSize: 64, color: 'gray' }} />
                  <Typography variant="body2" color="text.secondary">
                    Preview not available inline — use View File
                  </Typography>
                </Box>
              );
            }

            return (
              <iframe
                src={selectedPDF}
                title="File Viewer"
                width="100%"
                height="500px"
                frameBorder="0"
                style={{ display: pdfLoading ? 'none' : 'block' }}
                onLoad={() => setPdfLoading(false)}
              />
            );
          })()}
        </Card>
      </DialogAnimate>
    </Box>
  );
}

